import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Check, ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { ApiError, type StoreCategory, type TemplateCategory, type TemplateSubcategory } from "../../lib/api";
import { useCreateSite, useTemplate, useTemplates } from "../../lib/queries";
import { RESUME_PATH, clearPendingSite, loadPendingSite, savePendingSite } from "../../lib/pending-site";
import { useAuthStore } from "../../stores/auth";
import { CATEGORIES, categoryOf, findCategory, subcategoryLabel } from "../../lib/taxonomy";
import { AppLayout } from "../../components/layout/AppLayout";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";
import { Spinner } from "../../components/ui/Spinner";
import { ONLINE_STORE_COVER, STORE_CATEGORY_COVERS } from "../../lib/template-art";
import { STORE_CATEGORIES, storeCategoryLabel } from "../../lib/store-categories";
import {
  CategoryTile,
  SelectableTemplateCard,
} from "../../components/templates/TemplateCard";

/**
 * Onboarding wizard (PRD §183): what you do (category, then sub-category) →
 * design → name → create. The
 * site is created from a catalog template with the business info filled in
 * (PRD §18, §184); ?template=<id> deep links skip to the final step.
 *
 * Anyone can walk through the wizard. Creating the site needs an account:
 * a signed-out visitor's choices are kept while they sign up or log in, and
 * the site is created as soon as they come back (?resume=1).
 *
 * Online stores take a parallel path: "Online store" → what you sell and a
 * store design → name. The store is seeded with sample products for the
 * chosen category.
 */
export function OnboardingPage() {
  const [searchParams] = useSearchParams();
  const deepTemplateId = searchParams.get("template") ?? undefined;
  const deep = useTemplate(deepTemplateId);

  const navigate = useNavigate();
  const createSite = useCreateSite();
  const signedIn = useAuthStore((s) => s.status === "authenticated");
  const resuming = searchParams.get("resume") === "1";
  const resumed = useRef(false);

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [kind, setKind] = useState<"website" | "store">("website");
  // A website's category and sub-category; `category` is what a store sells.
  const [group, setGroup] = useState<TemplateCategory | null>(null);
  const [subcategory, setSubcategory] = useState<TemplateSubcategory | null>(null);
  const [category, setCategory] = useState<StoreCategory | null>(null);
  const [templateId, setTemplateId] = useState<string | null>(deepTemplateId ?? null);
  const [templateName, setTemplateName] = useState<string | null>(deep.data?.name ?? null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [description, setDescription] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const deepLoaded = useRef(false);
  const subcategoriesRef = useRef<HTMLHeadingElement>(null);

  // Bring a category's sub-categories into view once it is picked (on phones
  // they would otherwise appear below the fold).
  useEffect(() => {
    if (group) subcategoriesRef.current?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
  }, [group]);

  const deepError = Boolean(deepTemplateId) && Boolean(deep.error);
  const deepPending = Boolean(deepTemplateId) && deep.isPending;

  // A deep-linked template jumps the wizard to the naming step once loaded.
  useEffect(() => {
    if (deepLoaded.current || !deep.data) return;
    deepLoaded.current = true;
    setTemplateId(deep.data.id);
    setTemplateName(deep.data.name);
    if (deep.data.kind === "store") {
      // Store designs still need to know what the store sells; a design made
      // for one category starts with that category chosen.
      setKind("store");
      const only = deep.data.categories?.length === 1 ? deep.data.categories[0] : undefined;
      if (only) setCategory(only);
      setStep(2);
      return;
    }
    setGroup(deep.data.category ?? categoryOf(deep.data.subcategory)?.slug ?? null);
    setSubcategory(deep.data.subcategory ?? null);
    setStep(3);
  }, [deep.data]);

  // Back from signing up or logging in: restore their choices and create the
  // site they asked for. Signed-out (they gave up on signing in): just
  // restore the choices so nothing is lost.
  useEffect(() => {
    if (!resuming || resumed.current) return;
    resumed.current = true;
    const pending = loadPendingSite();
    if (!pending) return;
    setKind(pending.kind);
    setGroup(pending.group);
    setSubcategory(pending.subcategory);
    setCategory(pending.category);
    setTemplateId(pending.templateId);
    setTemplateName(pending.templateName);
    setName(pending.name);
    setEmail(pending.email);
    setPhone(pending.phone);
    setDescription(pending.description);
    setStep(3);
    if (signedIn) {
      void create({
        name: pending.name.trim(),
        templateId: pending.templateId,
        business: {
          ...(pending.email.trim() ? { email: pending.email.trim() } : {}),
          ...(pending.phone.trim() ? { phone: pending.phone.trim() } : {}),
          ...(pending.description.trim() ? { description: pending.description.trim() } : {}),
        },
        store: pending.kind === "store" ? pending.category : null,
      });
    }
  }, [resuming, signedIn]);

  if (deepPending) {
    return (
      <AppLayout>
        <div className="loading-center" style={{ minHeight: "60vh" }}>
          <Spinner size="lg" />
          <p className="text-muted">Loading template…</p>
        </div>
      </AppLayout>
    );
  }

  async function create(input: {
    name: string;
    templateId: string;
    business: { email?: string; phone?: string; description?: string };
    store?: StoreCategory | null;
  }) {
    try {
      await createSite.mutateAsync({
        name: input.name,
        templateId: input.templateId,
        business: Object.keys(input.business).length > 0 ? input.business : undefined,
        ...(input.store ? { store: { category: input.store } } : {}),
      });
      clearPendingSite();
      navigate("/app/dashboard", { replace: true });
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "Could not create the site.");
    }
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!templateId) {
      setFormError("Choose a design before creating your site.");
      return;
    }
    if (kind === "store" && !category) {
      setFormError("Choose what your store sells.");
      return;
    }
    const trimmed = name.trim();
    if (!trimmed) {
      setFormError("Give your site a name.");
      return;
    }
    setFormError(null);
    if (!signedIn) {
      // Creating needs an account: keep their choices, sign up, come back.
      savePendingSite({
        templateId, templateName, kind, group, subcategory, category,
        name: trimmed, email, phone, description,
      });
      navigate("/signup", { state: { from: RESUME_PATH } });
      return;
    }
    await create({
      name: trimmed,
      templateId,
      business: {
        ...(email.trim() ? { email: email.trim() } : {}),
        ...(phone.trim() ? { phone: phone.trim() } : {}),
        ...(description.trim() ? { description: description.trim() } : {}),
      },
      store: kind === "store" ? category : null,
    });
  }

  const steps = [
    { num: 1, label: "Your work" },
    { num: 2, label: "Design" },
    { num: 3, label: "Details" },
  ];

  return (
    <AppLayout>
      <main className="page" style={{ maxWidth: "56rem" }}>
        {/* Wizard Stepper */}
        <div className="wizard-stepper">
          {steps.map((s, i) => (
            <div key={s.num} style={{ display: "contents" }}>
              <div className={`wizard-step ${step === s.num ? "wizard-step--active" : step > s.num ? "wizard-step--done" : ""}`}>
                <div className="wizard-step__dot">
                  {step > s.num ? <Check size={14} /> : s.num}
                </div>
                <span className="wizard-step__label">{s.label}</span>
              </div>
              {i < steps.length - 1 ? (
                <div className={`wizard-step__connector ${step > s.num ? "wizard-step__connector--done" : ""}`} />
              ) : null}
            </div>
          ))}
        </div>

        <p className="eyebrow wizard-step-count">Step {step} of 3</p>
        <h1 style={{ marginBottom: "var(--z-space-2)" }}>
          {step === 1
            ? "What do you do?"
            : step === 2
              ? kind === "store"
                ? "What do you sell?"
                : "Choose a design"
              : kind === "store"
                ? "Name your store"
                : "Name your website"}
        </h1>
        <p className="text-muted" style={{ marginBottom: "var(--z-space-6)" }}>
          {step === 1 && "We'll show you designs made for your kind of work."}
          {step === 2 &&
            (kind === "store"
              ? "We'll fill your store with sample products you can edit or replace."
              : "Pick a template that fits your style.")}
          {step === 3 && "Almost there — give your site a name and you're ready."}
        </p>

        {deepError && step === 1 ? (
          <div className="form-error" role="alert" style={{ marginBottom: "var(--z-space-6)" }}>
            That template could not be found — pick another one below.
          </div>
        ) : null}

        {/* Step 1: Category, then sub-category */}
        {step === 1 ? (
          <section aria-label="What you do">
            <div className="profession-grid" role="group" aria-label="Categories">
              {CATEGORIES.map((option) => (
                <CategoryTile
                  key={option.slug}
                  image={option.cover}
                  label={option.label}
                  hint={option.hint}
                  pressed={kind === "website" && group === option.slug}
                  onClick={() => {
                    setKind("website");
                    if (group !== option.slug) setSubcategory(null);
                    setGroup(option.slug);
                  }}
                />
              ))}
            </div>
            {kind === "website" && findCategory(group) ? (
              <>
                <h2 className="onboarding-subhead" ref={subcategoriesRef}>
                  Which fits you best?
                </h2>
                <div className="profession-grid" role="group" aria-label={`${findCategory(group)!.label} sub-categories`}>
                  {findCategory(group)!.subcategories.map((option) => (
                    <CategoryTile
                      key={option.slug}
                      image={option.cover}
                      label={option.label}
                      hint={option.hint}
                      pressed={subcategory === option.slug}
                      onClick={() => setSubcategory(option.slug)}
                    />
                  ))}
                </div>
              </>
            ) : null}
            <h2 className="onboarding-subhead">Sell online</h2>
            <div className="profession-grid profession-grid--single" role="group" aria-label="Online store">
              <CategoryTile
                image={ONLINE_STORE_COVER}
                label="Online store"
                hint="Products, cart, checkout and payments"
                pressed={kind === "store"}
                onClick={() => {
                  setKind("store");
                  setGroup(null);
                  setSubcategory(null);
                }}
              />
            </div>
            <div className="wizard-actions">
              <Link className="btn btn--ghost" to={signedIn ? "/app/dashboard" : "/templates"}>
                <ChevronLeft size={16} />
                Cancel
              </Link>
              <Button
                variant="primary"
                disabled={kind === "website" && !subcategory}
                onClick={() => setStep(2)}
              >
                Next
                <ChevronRight size={16} />
              </Button>
            </div>
          </section>
        ) : null}

        {/* Step 2: What the store sells + store design */}
        {step === 2 && kind === "store" ? (
          <StorePicker
            category={category}
            onCategory={setCategory}
            selectedId={templateId}
            onSelect={(id, chosenName) => {
              setTemplateId(id);
              setTemplateName(chosenName);
            }}
            onClear={() => {
              setTemplateId(null);
              setTemplateName(null);
            }}
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
          />
        ) : null}

        {/* Step 2: Template */}
        {step === 2 && kind === "website" && subcategory ? (
          <TemplatePicker
            subcategory={subcategory}
            selectedId={templateId}
            onSelect={(id, chosenName) => {
              setTemplateId(id);
              setTemplateName(chosenName);
            }}
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
          />
        ) : null}

        {/* Step 3: Name */}
        {step === 3 ? (
          <section aria-label="Name your website">
            <div className="chosen-template">
              <Badge variant="primary">
                {kind === "store" && category
                  ? `${storeCategoryLabel(category)} store`
                  : subcategory
                    ? subcategoryLabel(subcategory)
                    : "Template"}
              </Badge>
              <p>
                Design: <strong>{templateName ?? "your chosen template"}</strong>
              </p>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => setStep(subcategory || kind === "store" ? 2 : 1)}
              >
                Change
              </button>
            </div>

            <form className="wizard-form" onSubmit={onSubmit}>
              <label>
                Website name
                <input
                  name="siteName"
                  className="form-input"
                  required
                  maxLength={100}
                  autoComplete="off"
                  placeholder="e.g. Acme Studio"
                  value={name}
                  onChange={(event) => {
                    setFormError(null);
                    setName(event.target.value);
                  }}
                />
              </label>

              <details className="business-details">
                <summary>Contact details (optional)</summary>
                <p className="text-muted text-sm" style={{ marginTop: "var(--z-space-2)" }}>
                  Filled into your site's contact info — you can change it later.
                </p>
                <label>
                  Email
                  <input
                    type="email"
                    name="businessEmail"
                    className="form-input"
                    autoComplete="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </label>
                <label>
                  Phone
                  <input
                    type="tel"
                    name="businessPhone"
                    className="form-input"
                    autoComplete="tel"
                    value={phone}
                    onChange={(event) => setPhone(event.target.value)}
                  />
                </label>
                <label>
                  Short description
                  <input
                    name="businessDescription"
                    className="form-input"
                    maxLength={170}
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                  />
                </label>
              </details>

              {formError ? (
                <div className="form-error" role="alert">
                  {formError}
                </div>
              ) : null}

              {!signedIn ? (
                <p className="text-muted text-sm" role="note">
                  Next you'll create a free account — just an email and a password — and your site
                  is created straight after.
                </p>
              ) : null}

              <div className="wizard-actions">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setStep(2)}
                  disabled={createSite.isPending}
                >
                  <ChevronLeft size={16} />
                  Back
                </Button>
                <Button type="submit" variant="primary" size="lg" loading={createSite.isPending}>
                  Create site
                  <ArrowRight size={16} />
                </Button>
              </div>
            </form>
          </section>
        ) : null}
      </main>
    </AppLayout>
  );
}

function TemplatePicker({
  subcategory,
  selectedId,
  onSelect,
  onBack,
  onNext,
}: {
  subcategory: TemplateSubcategory;
  selectedId: string | null;
  onSelect: (id: string, name: string) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const { data: templates, isPending, error } = useTemplates({ subcategory });

  return (
    <section aria-label="Choose a design">
      <p className="text-muted" style={{ marginBottom: "var(--z-space-4)" }}>
        Designs made for {subcategoryLabel(subcategory)}.
      </p>

      {isPending ? (
        <div className="loading-center" style={{ padding: "var(--z-space-12)" }}>
          <Spinner />
          <p className="text-muted">Loading designs…</p>
        </div>
      ) : null}

      {error ? (
        <div className="form-error" role="alert">
          Could not load templates.
        </div>
      ) : null}

      <div className="tpl-grid tpl-grid--compact">
        {(templates ?? []).filter((template) => template.kind !== "store").map((template) => (
          <SelectableTemplateCard
            key={template.id}
            template={template}
            selected={selectedId === template.id}
            onSelect={() => onSelect(template.id, template.name)}
          />
        ))}
      </div>

      <div className="wizard-actions">
        <Button variant="ghost" onClick={onBack}>
          <ChevronLeft size={16} />
          Back
        </Button>
        <Button
          variant="primary"
          disabled={!selectedId}
          onClick={onNext}
        >
          Next
          <ChevronRight size={16} />
        </Button>
      </div>
    </section>
  );
}

function StorePicker({
  category,
  onCategory,
  selectedId,
  onSelect,
  onClear,
  onBack,
  onNext,
}: {
  category: StoreCategory | null;
  onCategory: (category: StoreCategory) => void;
  selectedId: string | null;
  onSelect: (id: string, name: string) => void;
  onClear: () => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const { data: templates, isPending, error } = useTemplates();
  // Designs made for the chosen category first, then general store designs.
  const storeTemplates = (templates ?? [])
    .filter((t) => t.kind === "store")
    .filter((t) => !category || !t.categories?.length || t.categories.includes(category))
    .sort((a, b) => (b.categories?.length ?? 0) - (a.categories?.length ?? 0));
  const selectionFits = !selectedId || storeTemplates.some((t) => t.id === selectedId);

  // Switching category drops a design made for a different one.
  useEffect(() => {
    if (templates && category && !selectionFits) onClear();
  }, [templates, category, selectionFits, onClear]);

  return (
    <section aria-label="What do you sell">
      <div className="profession-grid" role="group" aria-label="Store categories">
        {STORE_CATEGORIES.map((option) => (
          <CategoryTile
            key={option.slug}
            image={STORE_CATEGORY_COVERS[option.slug]}
            label={option.label}
            hint={option.hint}
            pressed={category === option.slug}
            onClick={() => onCategory(option.slug)}
          />
        ))}
      </div>

      <h2 className="onboarding-subhead">
        {category ? `Designs for ${storeCategoryLabel(category).toLowerCase()} stores` : "Choose what you sell to see designs"}
      </h2>
      {isPending ? (
        <div className="loading-center" style={{ padding: "var(--z-space-8)" }}>
          <Spinner />
        </div>
      ) : null}
      {error ? (
        <div className="form-error" role="alert">
          Could not load store designs.
        </div>
      ) : null}
      <div className="tpl-grid tpl-grid--compact">
        {(category ? storeTemplates : []).map((template) => (
          <SelectableTemplateCard
            key={template.id}
            template={template}
            selected={selectedId === template.id}
            onSelect={() => onSelect(template.id, template.name)}
          />
        ))}
      </div>

      <div className="wizard-actions">
        <Button variant="ghost" onClick={onBack}>
          <ChevronLeft size={16} />
          Back
        </Button>
        <Button variant="primary" disabled={!category || !selectedId} onClick={onNext}>
          Next
          <ChevronRight size={16} />
        </Button>
      </div>
    </section>
  );
}
